import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PageHero, ACCENT, ACCENT_SOURDE } from "@/components/PageHero";
import { decalage } from "@/components/motion/Choregraphie";
import { trouverService } from "@/data/services-pages";
import { schemaService } from "./schema-service";
import { PROJETS } from "@/data/projets";
import { usePosts } from "@/hooks/useBlog";
import NotFound from "@/pages/NotFound";
import { articlesForPage } from "@/data/editorial-links";
import { SeoSocial } from "@/components/SeoSocial";

const SITE = "https://tokamdarius.ca";

/**
 * Les champs de prose longue portent leurs paragraphes separes par une ligne
 * vide, comme un fichier texte. Rendus tels quels dans un seul <p>, ils
 * sortiraient en un bloc de quatre cents mots : le JSON garde la coupe, le
 * composant la respecte.
 */
const paragraphes = (texte: string) =>
  texte
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

const PageService = () => {
  /**
   * Le segment complet, comme pour les villes. C'est l'aiguilleur qui a deja
   * tranche que ce slug appartient au catalogue des services ; la page le
   * relit pour aller chercher sa fiche, et retombe sur le 404 du site si elle
   * est montee hors de cette route.
   */
  const { page } = useParams();
  const service = trouverService(page ?? "");

  /**
   * Les articles du blog, pour la section preuves. Appele avant le retour
   * anticipe : un hook ne se declare pas sous condition. Au pre-rendu, la cle
   * ["posts"] est semee et les liens partent dans le HTML statique, ce qui est
   * tout l'objet de la manoeuvre : ouvrir un chemin de crawl.
   */
  const { data: articles = [] } = usePosts();

  /**
   * Le vrai 404 du site plutot qu'un message local : lui seul pose le noindex.
   * Sans ca, chaque slug invente sous /services deviendrait un soft 404
   * indexable.
   */
  if (!service) {
    return (
      <NotFound
        titre="Page introuvable"
        message="Ce service n'a pas de page dédiée. La page des services porte la liste complète."
      />
    );
  }

  const url = `${SITE}/services/${service.slug}`;

  /** Les fiches reelles derriere les slugs choisis pour ce service. */
  const projets = service.projetsMisEnAvant.flatMap(
    (slugProjet) => PROJETS.find((p) => p.slug === slugProjet) ?? []
  );

  const articlesLies = articlesForPage(articles, service.slug, "service");

  /**
   * Les services lies resolus en fiches. Un service annonce mais pas encore
   * redige se retire du maillage au lieu de poser un 404 interne, exactement
   * comme les villes voisines de PageVille.
   */
  const lies = service.servicesLies.flatMap(
    (slugLie) => trouverService(slugLie) ?? []
  );

  return (
    <div className="relative z-10 min-h-screen">
      <Header />
      <SeoSocial title={service.meta.title} description={service.meta.description} url={url} />

      <Helmet>
        <title>{service.meta.title}</title>
        <meta name="description" content={service.meta.description} />
        <link rel="canonical" href={url} />
        {/* Service avec areaServed sur la province, FAQPage et fil d'Ariane.
            Les sept questions de la page ne servaient qu'a l'oeil tant qu'elles
            n'etaient pas balisees, et misAJour ne sortait que dans le lastmod
            du sitemap : il ressort ici en dateModified, ce qui le corrobore. */}
        <script type="application/ld+json">{JSON.stringify(schemaService(service))}</script>
      </Helmet>

      <main className="pb-16">
        <PageHero
          surtitre="Service"
          titre={
            <>
              {service.nom}
              <br />
              <span style={{ color: ACCENT }}>au Québec</span>
            </>
          }
          soustitre={service.redaction.accroche}
          actions={
            <>
              <Link to="/#contact">
                <Button
                  size="lg"
                  className="h-12 rounded-full px-7 text-[0.95rem] font-medium text-white"
                  style={{ background: ACCENT }}
                >
                  Discuter de votre projet
                </Button>
              </Link>
              <Link to="/projets">
                {/* Variante ghost plutot que outline : outline traine un
                    backdrop-blur-sm, interdit ici. Le reste des classes donne
                    le meme bouton que sur /services, sans le flou. */}
                <Button
                  size="lg"
                  variant="ghost"
                  className="h-12 rounded-full border border-white/20 bg-transparent px-7 text-[0.95rem] font-medium text-white hover:bg-white/10 hover:text-white"
                >
                  Voir mes réalisations
                </Button>
              </Link>
            </>
          }
        />

        {/* Reponse extractible. Premier bloc de contenu, 40 a 60 mots, sans
            argumentaire : c'est ce paragraphe que les moteurs generatifs
            decoupent pour citer. */}
        <section className="container mx-auto px-6">
          <div
            data-reveal
            className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10"
          >
            <p className="text-lg leading-relaxed text-white/80">
              {service.redaction.reponseExtractible}
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden px-6 py-20">
          {/* Decor fixe. Pas de GlassDecor, pas de derive en boucle : une
              boucle infinie occupe le compositeur pour toujours, et le LCP
              mobile est deja a 3,9 s. Le mouvement de la page vient des
              entrees au defilement, qui ne se jouent qu'une fois. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(60% 40% at 85% 12%, ${ACCENT_SOURDE} 0%, transparent 70%)`,
              opacity: 0.16,
            }}
          />

          <div className="container relative mx-auto max-w-3xl">
            <h2 data-reveal className="text-3xl font-bold md:text-4xl">
              Le problème que ça règle
            </h2>
            {/* Le premier paragraphe entre un cran apres son titre : ils sont
                colles l'un a l'autre et franchissent la ligne ensemble, donc
                le decalage se voit. Les suivants entrent chacun pour eux,
                sans retard ajoute : un paragraphe fait cinq a six lignes, le
                suivant est 160 px plus bas et arrive bien apres, tout seul.
                Lui poser un delai ne le decale de rien, ca ne fait que le
                laisser vide un moment de plus a l'ecran. */}
            {paragraphes(service.probleme).map((texte, index) => (
              <p
                key={texte.slice(0, 40)}
                data-reveal
                style={index === 0 ? decalage(1) : undefined}
                className="mt-6 text-base leading-relaxed text-white/70"
              >
                {texte}
              </p>
            ))}

            {/* Ce que ce n'est pas.
                Le differenciateur de la page. Les concurrents releves sur ce
                SERP vendent tout a tout le monde ; aucun ne dit ce qu'il
                refuse. Dire non par ecrit qualifie le prospect avant l'appel
                et donne a la page un passage citable que personne d'autre
                n'offre. Les lignes viennent de la donnee, mot pour mot, et
                PageService.test.tsx verifie qu'elles sont bien rendues. */}
            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Ce que ce n'est pas
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              Un mandat mal orienté coûte plus cher à tout le monde qu'un mandat
              refusé au premier appel. Voici donc ce que je ne vends pas, et ce que
              je dis quand la demande ne colle pas avec ce que je sais faire.
            </p>
            {/* La cascade se pose sur les lignes, pas sur la liste : c'est ce
                qui les fait lire une par une au lieu d'entrer en bloc. */}
            <ul className="mt-6 space-y-4 text-white/70">
              {service.cequeCeNestPas.map((ligne, index) => (
                <li
                  key={ligne.slice(0, 40)}
                  data-reveal
                  style={decalage(index)}
                  className="flex gap-3"
                >
                  {/* Meme pastille ronde que le surtitre de PageHero. */}
                  <span
                    aria-hidden
                    className="mt-[0.6rem] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: ACCENT }}
                  />
                  <span>{ligne}</span>
                </li>
              ))}
            </ul>

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Ce que vous recevez
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              Le livrable n'est ni une maquette ni un rapport. C'est un logiciel qui
              tourne, avec ce qu'il faut autour pour qu'il continue de tourner le jour
              où je ne suis plus au dossier.
            </p>
            <ul className="mt-6 space-y-3 text-white/70">
              {service.livrables.map((livrable, index) => (
                <li
                  key={livrable}
                  data-reveal
                  style={decalage(index)}
                  className="flex gap-3"
                >
                  <span
                    aria-hidden
                    className="mt-[0.6rem] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: ACCENT }}
                  />
                  <span>{livrable}</span>
                </li>
              ))}
            </ul>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              L'outillage reste le même d'un projet à l'autre. Une pile réduite se
              maintient et se transmet ; une pile choisie pour être à la mode se paie
              deux ans plus tard, quand plus personne ne veut y toucher.
            </p>
            {/* Les pastilles arrivent une a une, de gauche a droite. Aucun
                survol ici : rien n'est cliquable, et un etat de survol sur un
                element inerte promet un clic qui n'existe pas. */}
            <ul className="mt-5 flex flex-wrap gap-2">
              {service.stack.map((outil, index) => (
                <li
                  key={outil}
                  data-reveal
                  style={decalage(index)}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-sm text-white/70"
                >
                  {outil}
                </li>
              ))}
            </ul>

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Comment ça se passe
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              Toujours dans cet ordre, et vous voyez sortir quelque chose de chaque
              étape. Personne ne disparaît trois mois pour revenir avec une surprise.
            </p>
            {/* Les etapes entrent dans l'ordre ou elles se deroulent, chacune
                a son passage. Pas de decalage entre elles : un titre, quatre
                lignes de description et 32 px de separation mettent l'etape
                suivante hors du champ quand celle-ci arrive. Le numero de
                l'etape porte deja l'ordre, l'animation n'a pas a le repeter
                en faisant patienter. */}
            <ol className="mt-6 space-y-8">
              {service.processus.map((etape, index) => (
                <li
                  key={etape.etape}
                  data-reveal
                  className="flex gap-4"
                >
                  <span
                    aria-hidden
                    className="mt-1 shrink-0 text-sm font-semibold tabular-nums"
                    style={{ color: ACCENT }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{etape.etape}</h3>
                    <p className="mt-2 text-base leading-relaxed text-white/70">
                      {etape.description}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            {/* ECART AVEC LE PLAN, assume et signale ici. La structure imposee
                a l'etape 3 de la tache 10 nomme neuf sections et n'en donne
                aucune a redaction.corps, un champ que le type de la tache 9
                declare pourtant. Sans place, la moitie de la prose du service
                serait morte dans le JSON et la page tomberait sous sa cible de
                mots. Le corps est donc rendu ici, entre le processus et les
                preuves, ou il conduit naturellement aux projets qu'il cite. */}
            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Ce que ça change une fois en ligne
            </h2>
            {/* Meme regle que plus haut : le premier paragraphe suit son titre
                d'un cran, les cinq autres entrent sans retard. Ce bloc fait
                environ 1 100 px de prose, aucun de ces paragraphes n'est a
                l'ecran en meme temps que son voisin. */}
            {paragraphes(service.redaction.corps).map((texte, index) => (
              <p
                key={texte.slice(0, 40)}
                data-reveal
                style={index === 0 ? decalage(1) : undefined}
                className="mt-6 text-base leading-relaxed text-white/70"
              >
                {texte}
              </p>
            ))}

            {/* Preuves. Meme role que sur les pages villes : ouvrir un chemin
                de crawl vers les fiches projets, que Google a ecartees en
                "Discovered, currently not indexed". */}
            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Ce que j'ai construit
            </h2>
            <p
              data-reveal
              style={decalage(1)}
              className="mt-6 text-base leading-relaxed text-white/70"
            >
              Des applications en ligne, que vous pouvez ouvrir et juger vous-même.
              J'ai écrit le code de chacune, du premier écran à la mise en production.
            </p>
            <ul className="mt-6 space-y-4">
              {projets.map((projet, index) => (
                <li
                  key={projet.slug}
                  /* La meme chute que les cartes de /projets : elles tombent
                     du haut, penchees, et se posent en rebondissant.
                     L'inclinaison alterne, sinon deux cartes qui tombent
                     exactement pareil se lisent comme un rideau qui descend.
                     La chute est portee par le li et le survol par le lien :
                     les deux ne se disputent jamais le meme transform. */
                  data-reveal="chute"
                  style={{
                    ["--rev-delai" as string]: `${Math.min(index, 5) * 110}ms`,
                    ["--rev-rot" as string]: index % 2 === 0 ? "-1.6deg" : "1.6deg",
                  }}
                >
                  <Link
                    to={`/projets/${projet.slug}`}
                    className="block rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.07]"
                  >
                    <h3 className="text-lg font-semibold text-white">{projet.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      {projet.tagline}
                    </p>
                    <p className="mt-3 text-xs text-white/40">{projet.stack.join(", ")}</p>
                  </Link>
                </li>
              ))}
            </ul>
            <p data-reveal className="mt-6 text-white/60">
              Le reste est sur la page des{" "}
              <Link
                to="/projets"
                className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
              >
                réalisations
              </Link>
              .
            </p>

            {articlesLies.length > 0 && (
              <div className="mt-8">
                <p data-reveal className="text-base leading-relaxed text-white/70">
                  Lectures du journal liées à ce service :
                </p>
                <ul className="mt-3 space-y-2 text-white/70">
                  {articlesLies.map((article, index) => (
                    <li key={article.slug} data-reveal style={decalage(index + 1)}>
                      <Link
                        to={`/blog/${article.slug}`}
                        className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
                      >
                        {article.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h2 data-reveal className="mt-16 text-3xl font-bold md:text-4xl">
              Questions fréquentes
            </h2>
            {/* Chaque question entre pour elle-meme, sans decalage entre
                elles. Sept blocs de trois a cinq lignes espaces de 32 px :
                aucun n'est a l'ecran quand le precedent arrive, donc un delai
                ne decalerait rien et ferait attendre une reponse deja
                visible. */}
            <dl className="mt-6 space-y-8">
              {service.faq.map((item) => (
                <div key={item.question} data-reveal>
                  <dt className="text-lg font-semibold text-white">{item.question}</dt>
                  <dd className="mt-3 text-base leading-relaxed text-white/70">
                    {item.reponse}
                  </dd>
                </div>
              ))}
            </dl>

            <p data-reveal className="mt-16 text-white/60">
              {lies.length > 0 && (
                <>
                  Voir aussi :{" "}
                  {lies.map((lie, index) => (
                    <span key={lie.slug}>
                      {index > 0 && ", "}
                      <Link
                        to={`/services/${lie.slug}`}
                        className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
                      >
                        {lie.nom}
                      </Link>
                    </span>
                  ))}
                  .{" "}
                </>
              )}
              Tous mes{" "}
              <Link
                to="/services"
                className="underline underline-offset-4 decoration-white/30 transition-colors hover:text-white hover:decoration-white"
              >
                services
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden">
          {/* Meme lueur que la cloture de /services, mais figee : la classe
              decor-derive et sa duree d'animation sont retirees. */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute"
              style={{
                top: "-45%",
                left: "8%",
                width: "48vw",
                height: "48vw",
                background: `radial-gradient(circle, ${ACCENT_SOURDE} 0%, transparent 62%)`,
                opacity: 0.22,
                filter: "blur(100px)",
              }}
            />
          </div>
          <div className="container relative mx-auto px-6 py-20 sm:py-28">
            <div
              data-reveal
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 sm:p-10"
            >
              <h2 className="text-[clamp(1.8rem,3.2vw,2.4rem)] font-bold leading-tight tracking-tight text-white">
                Parlons de ce que vous voulez construire
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-white/60">
                Le premier appel sert à savoir si le sur-mesure est la bonne réponse à
                votre problème. Si ce n'en est pas une, je le dis, et vous aurez perdu
                une demi-heure plutôt qu'un budget.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/#contact">
                  <Button
                    size="lg"
                    className="h-12 rounded-full px-7 text-[0.95rem] font-medium text-white transition-transform hover:scale-[1.02]"
                    style={{ background: ACCENT, boxShadow: `0 12px 40px -12px ${ACCENT}` }}
                  >
                    Me contacter
                  </Button>
                </Link>
                <a
                  href="mailto:tokamdarius@gmail.com"
                  className="text-[0.95rem] font-medium text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  tokamdarius@gmail.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageService;
